/** Canvas selection overlays, toasts, and part thumbnails. HTML controls live in chrome.ts. */
import { getSprite, renderAtlas } from '../engine/atlas'
import {
  drawText as drawFontText,
  textWidth as fontTextWidth,
} from '../engine/font'
import { Terrain } from '../game/types'
import {
  blockUseCount,
  ITEM_FOR_PAYLOAD,
  type ObjectDef,
} from '../game/objects'
import type { PlacedObject } from '../game/level'
import { VIEW, type Rect } from './viewport'

/** 5x7 glyphs, 1px of letterspacing: 6px per character. */
export const CHAR_ADV = 6
export const CHAR_H = 7
/** Longest line that fits the framebuffer. */
export const MAX_CHARS = Math.floor((VIEW.w - 6) / CHAR_ADV)

/** Truncate to `max` characters, ending with an ellipsis when it has to cut. */
export function clipText(text: string, max: number): string {
  if (max <= 0) return ''
  if (text.length <= max) return text
  if (max === 1) return text.slice(0, 1)
  return text.slice(0, max - 1) + '.'
}

/** Width of a string in framebuffer pixels. */
export function textWidth(text: string): number {
  return fontTextWidth(text, 1)
}

/** Draw text centred on `cx`. */
export function drawTextCentre(
  ctx: CanvasRenderingContext2D,
  text: string,
  cx: number,
  y: number,
  color: string,
): void {
  drawFontText(ctx, text, Math.round(cx - textWidth(text) / 2), y, color, {
    outline: null,
  })
}

// ── primitives ─────────────────────────────────────────────────────────────

export function fillRect(
  ctx: CanvasRenderingContext2D,
  r: Rect,
  color: string,
): void {
  ctx.fillStyle = color
  ctx.fillRect(r.x, r.y, r.w, r.h)
}

export function strokeRect(
  ctx: CanvasRenderingContext2D,
  r: Rect,
  color: string,
): void {
  ctx.fillStyle = color
  ctx.fillRect(r.x, r.y, r.w, 1)
  ctx.fillRect(r.x, r.y + r.h - 1, r.w, 1)
  ctx.fillRect(r.x, r.y + 1, 1, r.h - 2)
  ctx.fillRect(r.x + r.w - 1, r.y + 1, 1, r.h - 2)
}

/** Where a tile rectangle lands on screen. */
export function tileRectToScreen(
  view: Rect,
  camX: number,
  camY: number,
  tx: number,
  ty: number,
  w = 1,
  h = 1,
): Rect {
  return {
    x: view.x + tx * 16 - camX,
    y: view.y + ty * 16 - camY,
    w: w * 16,
    h: h * 16,
  }
}

/** Translucent footprint preview. */
export function ghostTile(
  ctx: CanvasRenderingContext2D,
  r: Rect,
  color: string,
  alpha = 0.3,
): void {
  ctx.fillStyle = color
  ctx.globalAlpha = alpha
  ctx.fillRect(r.x + 1, r.y + 1, r.w - 2, r.h - 2)
  ctx.globalAlpha = 1
}

/** Outline plus corner ticks: the drag preview. */
export function cursorTile(
  ctx: CanvasRenderingContext2D,
  r: Rect,
  color: string,
): void {
  strokeRect(ctx, r, color)
  ctx.fillStyle = color
  const t = 3
  ctx.fillRect(r.x, r.y, t, 1)
  ctx.fillRect(r.x, r.y, 1, t)
  ctx.fillRect(r.x + r.w - t, r.y, t, 1)
  ctx.fillRect(r.x + r.w - 1, r.y, 1, t)
  ctx.fillRect(r.x, r.y + r.h - 1, t, 1)
  ctx.fillRect(r.x, r.y + r.h - t, 1, t)
  ctx.fillRect(r.x + r.w - t, r.y + r.h - 1, t, 1)
  ctx.fillRect(r.x + r.w - 1, r.y + r.h - t, 1, t)
}

// ── icons ──────────────────────────────────────────────────────────────────

/**
 * Blit an atlas sprite scaled to fit an `s`x`s` box, nearest neighbour. Returns
 * false when the key has no art, so callers can fall back to a swatch instead of
 * showing the atlas's magenta debug pixel.
 */
export function drawSpriteFitted(
  ctx: CanvasRenderingContext2D,
  key: string,
  x: number,
  y: number,
  s: number,
): boolean {
  const sprite = getSprite(key)
  if (sprite === undefined) return false
  const scale = Math.min(s / sprite.w, s / sprite.h)
  const w = Math.max(1, Math.round(sprite.w * scale))
  const h = Math.max(1, Math.round(sprite.h * scale))
  const img = renderAtlas(sprite)
  ctx.save()
  ctx.imageSmoothingEnabled = false
  ctx.drawImage(
    img,
    Math.round(x + (s - w) / 2),
    Math.round(y + (s - h) / 2),
    w,
    h,
  )
  ctx.restore()
  return true
}

/** Terrain tile -> the sprite the renderer uses for it. */
const TERRAIN_SPRITE: Record<number, string> = {
  [Terrain.GROUND]: 'ground-top',
  [Terrain.HARD]: 'hard-block',
  [Terrain.BRICK]: 'brick',
  [Terrain.SLOPE_L]: 'slope-left',
  [Terrain.SLOPE_R]: 'slope-right',
  [Terrain.PLATFORM]: 'platform',
}

/**
 * A palette cell for a terrain id: the real tile, or a colour swatch.
 *
 * The swatch is the cell's own colour, passed in by the caller. It used to be
 * looked up from a second table here -- a copy of the brush colours from
 * `tools.ts`, which had to agree with them and did not have to. One cell, one
 * colour, one place to read it from.
 */
export function drawTerrainIcon(
  ctx: CanvasRenderingContext2D,
  terrain: number,
  x: number,
  y: number,
  s: number,
  tint: string,
): void {
  if (terrain === Terrain.EMPTY) {
    // Faint dot grid reads as "nothing here".
    ctx.fillStyle = '#1c1c2c'
    for (let dy = 2; dy < s; dy += 4) {
      for (let dx = 2; dx < s; dx += 4) ctx.fillRect(x + dx, y + dy, 1, 1)
    }
    return
  }
  const key = TERRAIN_SPRITE[terrain]
  if (key !== undefined && drawSpriteFitted(ctx, key, x, y, s)) return
  fillRect(ctx, { x, y, w: s, h: s }, tint)
  strokeRect(ctx, { x, y, w: s, h: s }, '#101018')
}

/**
 * A palette cell for an object: its real sprite where the art exists, else the
 * cell's category tint with a two letter code. Either way the cell is
 * recognisable before the art modules are finished.
 */
export function drawObjectIcon(
  ctx: CanvasRenderingContext2D,
  def: ObjectDef,
  code: string,
  x: number,
  y: number,
  s: number,
  tint: string,
): void {
  if (def.id === 'pipe') {
    for (let row = 0; row < 2; row++)
      for (let col = 0; col < 2; col++) {
        drawSpriteFitted(
          ctx,
          `pipe-${row === 0 ? 'top' : 'body'}-${col === 0 ? 'left' : 'right'}`,
          x + (col * s) / 2,
          y + (row * s) / 2,
          s / 2,
        )
      }
    return
  }
  if (drawSpriteFitted(ctx, def.sprite, x, y, s)) return
  fillRect(ctx, { x, y, w: s, h: s }, tint)
  // Lethal objects get a hazard stripe; the rest stay plain.
  if (def.lethal === true) {
    fillRect(ctx, { x, y, w: s, h: 1 }, '#fcfcfc')
    fillRect(ctx, { x, y: y + s - 1, w: s, h: 1 }, '#fcfcfc')
  }
  drawTextCentre(
    ctx,
    code.slice(0, 2),
    x + s / 2,
    y + Math.floor((s - CHAR_H) / 2),
    '#101018',
  )
}

// ── level view overlay ─────────────────────────────────────────────────────

/** The hover cursor, in the renderer's coordinate space. */
export interface CursorBox {
  tx: number
  ty: number
  w: number
  h: number
  ok: boolean
}

/**
 * One loaded block, reduced to what its badge needs.
 *
 * Built by `payloadBadges` rather than read off the level inside `drawOverlay`,
 * so the resolver is testable without a canvas.
 */
export interface PayloadBadge {
  tx: number
  ty: number
  w: number
  h: number
  /** Sprite key of the item inside. */
  sprite: string
  /** Payouts the block holds, drawn beside the icon when greater than one. */
  count: number
}

/**
 * The badges for every loaded container in a level.
 *
 * Hidden caches are included on purpose: the editor is the one place a hidden
 * block is meant to be seen, and what it holds should be as legible there as a
 * visible crate's. A block whose `contains` names nothing is skipped — the
 * warning about that value belongs to the behaviour, and a badge cannot draw a
 * payload that does not exist.
 */
export function payloadBadges(
  objects: readonly PlacedObject[],
): PayloadBadge[] {
  const out: PayloadBadge[] = []
  for (const o of objects) {
    if (!o.alive || o.def.container !== true) continue
    const token = o.props['contains']
    const item =
      typeof token === 'string' ? ITEM_FOR_PAYLOAD.get(token) : undefined
    if (item === undefined) continue
    out.push({
      tx: o.tx,
      ty: o.ty,
      w: o.def.w,
      h: o.def.h,
      sprite: item.sprite,
      count: blockUseCount(o.props['count']) ?? 1,
    })
  }
  return out
}

/**
 * The live drag, reduced to the fields the overlay needs. `Editor`'s drag state
 * satisfies this structurally.
 */
export interface DragPreview {
  mode: 'paint' | 'fill' | 'move'
  startTx: number
  startTy: number
  curTx: number
  curTy: number
  originTx: number
  originTy: number
  moveW: number
  moveH: number
}

export interface OverlayView {
  /** The view rect in the renderer's own (translated) coordinates. */
  view: Rect
  camX: number
  camY: number
  startTx: number
  startTy: number
  cursor: CursorBox | null
  drag: DragPreview | null
  /** Loaded blocks, marked with what they hold. */
  badges: PayloadBadge[]
  time: number
}

/**
 * The editor's own overlay: the start marker (the renderer draws the grid and
 * the hover cursor), the badges on loaded blocks, and the drag previews.
 */
export function drawOverlay(
  ctx: CanvasRenderingContext2D,
  o: OverlayView,
): void {
  drawStartMarker(ctx, o.view, o.camX, o.camY, o.startTx, o.startTy, o.time)
  drawPayloadBadges(ctx, o)

  const drag = o.drag
  if (drag === null) return
  if (drag.mode === 'fill') {
    const w = Math.abs(drag.curTx - drag.startTx) + 1
    const h = Math.abs(drag.curTy - drag.startTy) + 1
    const r = tileRectToScreen(
      o.view,
      o.camX,
      o.camY,
      Math.min(drag.startTx, drag.curTx),
      Math.min(drag.startTy, drag.curTy),
      w,
      h,
    )
    ghostTile(ctx, r, '#3cbcfc', 0.25)
    cursorTile(ctx, r, '#3cbcfc')
    drawTextCentre(
      ctx,
      `${w}X${h}`,
      r.x + r.w / 2,
      Math.max(0, r.y - 8),
      '#3cbcfc',
    )
    return
  }
  if (drag.mode === 'move') {
    const r = tileRectToScreen(
      o.view,
      o.camX,
      o.camY,
      drag.curTx,
      drag.curTy,
      drag.moveW,
      drag.moveH,
    )
    const from = tileRectToScreen(
      o.view,
      o.camX,
      o.camY,
      drag.originTx,
      drag.originTy,
    )
    ghostTile(ctx, r, '#85ded1', 0.3)
    cursorTile(ctx, r, '#85ded1')
    strokeRect(ctx, from, 'rgba(252,216,0,0.5)')
  }
}

/** Badge side in framebuffer pixels: over half a tile, so it reads at a glance. */
const BADGE_SIZE = 10

/**
 * Mark loaded blocks: the item's own sprite on a dark plate, with the payout
 * count beside it when the block holds more than one.
 *
 * The icon sits just above its block, the way a builder marks what a block
 * holds, and flips inside the tile when there is no room above, so a block on
 * the top visible row is still labelled.
 */
function drawPayloadBadges(
  ctx: CanvasRenderingContext2D,
  o: OverlayView,
): void {
  for (const b of o.badges) {
    const r = tileRectToScreen(o.view, o.camX, o.camY, b.tx, b.ty, b.w, b.h)
    if (r.x + r.w < o.view.x || r.x > o.view.x + o.view.w) continue
    const above = r.y - BADGE_SIZE - 1 >= o.view.y
    const y = above ? r.y - BADGE_SIZE - 1 : r.y + 2
    if (y + BADGE_SIZE > o.view.y + o.view.h) continue
    const x = r.x + 2
    fillRect(
      ctx,
      { x: x - 1, y: y - 1, w: BADGE_SIZE + 2, h: BADGE_SIZE + 2 },
      'rgba(6,6,14,0.75)',
    )
    drawSpriteFitted(ctx, b.sprite, x, y, BADGE_SIZE)
    if (b.count > 1) {
      drawFontText(ctx, `X${b.count}`, x + BADGE_SIZE + 2, y + 2, '#fcfcfc', {
        outline: '#101018',
      })
    }
  }
}

/** A short-lived centred message, e.g. "FILLED 48". */
export function drawToast(
  ctx: CanvasRenderingContext2D,
  view: Rect,
  text: string,
): void {
  const w = textWidth(text) + 10
  const box: Rect = {
    x: Math.round(view.x + (view.w - w) / 2),
    y: view.y + view.h - 24,
    w,
    h: CHAR_H + 4,
  }
  fillRect(ctx, box, 'rgba(6,6,14,0.88)')
  strokeRect(ctx, box, '#85ded1')
  drawTextCentre(ctx, text, box.x + box.w / 2, box.y + 2, '#85ded1')
}

/**
 * The player start marker, blinking so it is findable on a busy level. The flag
 * sits on a pole that rises out of the start tile.
 */
export function drawStartMarker(
  ctx: CanvasRenderingContext2D,
  view: Rect,
  camX: number,
  camY: number,
  tx: number,
  ty: number,
  time: number,
): void {
  const r = tileRectToScreen(view, camX, camY, tx, ty)
  if (r.x + r.w < view.x || r.x > view.x + view.w) return
  if (r.y + r.h < view.y || r.y > view.y + view.h) return
  const blink = Math.floor(time / 320) % 2 === 0
  const colour = blink ? '#00d800' : '#006800'
  // Pole from the tile up, then a stepped flag so it reads at 1x.
  ctx.fillStyle = colour
  ctx.fillRect(r.x + 2, r.y - 16 + 4, 1, 16 + 12)
  ctx.beginPath()
  ctx.rect(r.x + 3, r.y - 16 + 4, 7, 1)
  ctx.rect(r.x + 3, r.y - 16 + 5, 6, 1)
  ctx.rect(r.x + 3, r.y - 16 + 6, 5, 1)
  ctx.rect(r.x + 3, r.y - 16 + 7, 4, 1)
  ctx.rect(r.x + 3, r.y - 16 + 8, 3, 1)
  ctx.fill()
  cursorTile(ctx, r, blink ? '#85ded1' : '#a07000')
}
